import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FPMSyncType } from '../../../common/enums';
import {
    DrawingObject,
    DrawingObjectBase,
    DrawingObjectCategory,
    DrawingObjectCenterStyle,
    DrawingObjectCenterUnits,
    DrawingObjectCircle,
    DrawingObjectFillStyle,
    DrawingObjectLabelAlignment,
    DrawingObjectLineType,
    DrawingObjectOffsetUnits,
    DrawingObjectPolygon,
    DrawingObjectRectangle,
    DrawingObjectType
} from '../../../components/drawing-object/types';
import {
    FEWebDrawingGet,
    DrawingObject as ApiDrawingObject,
    DayOfWeek,
    DrawingObjectGeometry as ApiDrawingObjectGeometry,
    DrawingObjectsService,
} from '../../../services/axios/axios-api-v2-generated';
import type { RootState } from '.';
import { type AppThunk } from '../store';

type DrawingObjectsSlice = {
    objects: DrawingObject[];
};

const initialState: DrawingObjectsSlice = {
    objects: []
};

export const drawingObjectsSlice = createSlice({
    name: 'drawingObjects',
    initialState: initialState,
    reducers: {
        initDrawingObjects(state, params: PayloadAction<DrawingObject[]>) {
            state.objects = params.payload;
        },
        updateDrawingObjects(state, params: PayloadAction<{ updated: DrawingObject[]; deleted: string[] }>) {
            if (params.payload.deleted.length > 0) {
                state.objects = state.objects.filter((x) => !params.payload.deleted.includes(x.id));
            }

            for (const update of params.payload.updated) {
                const idx = state.objects.findIndex((x) => x.id === update.id);
                if (idx !== -1) {
                    state.objects[idx] = update;
                } else {
                    state.objects.push(update);
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(createDrawingObject.fulfilled, (state, action) => {
            const result = action.payload;
            if (!result.success) {
                return;
            }
            state.objects.push(result.object as DrawingObject);
        });

        builder.addCase(updateDrawingObject.fulfilled, (state, action) => {
            if (!action.payload.success) {
                return;
            }
            const idx = state.objects.findIndex((x) => x.id === action.meta.arg.id);
            if (idx !== -1) {
                state.objects[idx] = action.meta.arg;
            }
        });

        builder.addCase(deleteDrawingObject.fulfilled, (state, action) => {
            if (!action.payload.success) {
                return;
            }
            state.objects = state.objects.filter((x) => x.id !== action.meta.arg);
        });
    }
});

export const initDrawingObjects = (params: FEWebDrawingGet[]): AppThunk => {
    return (dispatch) => {
        dispatch(drawingObjectsSlice.actions.initDrawingObjects(params.map(fromApi)));
    };
};
export const updateDrawingObjects = (params: { updated: FEWebDrawingGet[]; deleted: string[] }): AppThunk => {
    return (dispatch) => {
        dispatch(
            drawingObjectsSlice.actions.updateDrawingObjects({
                ...params,
                updated: params.updated.map(fromApi)
            })
        );
    };
};

export const createDrawingObject = createAsyncThunk('drawing-objects/create', async (params: Omit<DrawingObject, 'id'>, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        if (state.drawingObjects.objects.some((obj) => obj.name === params.name)) {
            return { success: false, duplicateName: true };
        }

        const result = await DrawingObjectsService.createAsync({ body: toApi(params as DrawingObject) }, { withAuthorization: true });

        return {
            success: true,
            object: {
                ...params,
                id: result.id!
            }
        };
    } catch {
        return {
            success: false
        };
    }
});

export const updateDrawingObject = createAsyncThunk('drawing-objects/update', async (params: DrawingObject, thunkAPI) => {
    try {
        const state = thunkAPI.getState() as RootState;
        if (
            state.drawingObjects.objects.some(
                (obj) => obj.name === params.name && obj.isCompany === params.isCompany && obj.id !== params.id
            )
        ) {
            return { success: false, duplicateName: true };
        }

        await DrawingObjectsService.updateAsync({ id: params.id, body: toApi(params as DrawingObject) }, { withAuthorization: true });

        return { success: true, object: params };
    } catch {
        return { success: false };
    }
});

export const deleteDrawingObject = createAsyncThunk('drawing-objects/delete', async (params: string, thunkAPI) => {
    try {
        await DrawingObjectsService.deleteAsync({ id: params }, { withAuthorization: true });
        return { success: true };
    } catch {
        return { success: false };
    }
});

function fromApi(input: FEWebDrawingGet): DrawingObject {
    const base: DrawingObjectBase = {
        id: input.object!.id!,
        name: input.object!.name!,
        isCompany: input.object?.isCompany ?? false,
        label: {
            text: input.object?.label?.text ?? '',
            alignment: input.object?.label?.alignment ?? DrawingObjectLabelAlignment.left,
            color: input.object?.label?.color ?? ''
        },
        line: {
            style: input.object?.line?.style ?? DrawingObjectLineType.solid,
            width: input.object?.line?.width ?? 0,
            color: input.object?.line?.color ?? ''
        },
        fill: {
            color: input.object?.fill?.color ?? '',
            style: input.object?.fill?.style ?? DrawingObjectFillStyle.none
        },
        center: {
            style: input.object?.center?.style ?? DrawingObjectCenterStyle.none,
            color: input.object?.center?.color ?? '',
            size: input.object?.center?.size ?? 0
        },
        zoom: {
            object: input.object?.zoom?.object ?? 0,
            point: input.object?.zoom?.point ?? 0,
            label: input.object?.zoom?.label ?? 0
        },
        category: input.object?.category ?? DrawingObjectCategory.viewBased,
        altitude: {
            min: input.object?.altitude?.min ?? null,
            max: input.object?.altitude?.max ?? null
        },
        alerts: {
            plane: input.object?.alerts?.plane ?? false,
            route: input.object?.alerts?.route ?? false
        },
        fpmProperties: {
            sync: input.object?.fpmProperties?.sync ?? false,
            FPMSyncType: input.object?.fpmProperties?.FPMSyncType ?? FPMSyncType.global
        },
        validTime: {
            startDay: input.object?.validTime?.startDay ?? null,
            endDay: input.object?.validTime?.endDay ?? null,
            startTime: input.object?.validTime?.startTime ?? null,
            endTime: input.object?.validTime?.endTime ?? null,
            activeDaysOfWeek: input.object?.validTime?.activeDaysOfWeek ?? 0,
            greyOutNonActive: input.object?.validTime?.greyOutNonActive ?? false
        }
    };

    if (input.object?.type === DrawingObjectType.circle) {
        return {
            ...base,
            type: DrawingObjectType.circle,
            geometry: {
                centerUnits: input.object?.geometry?.centerUnits ?? DrawingObjectCenterUnits.latLng,
                centerX: input.object?.geometry?.centerX ?? 0,
                centerY: input.object?.geometry?.centerY ?? 0,
                radiusUnits: input.object?.geometry?.radiusUnits ?? DrawingObjectOffsetUnits.miles,
                radiusX: input.object?.geometry?.radiusX ?? 0,
                radiusY: input.object?.geometry?.radiusY ?? 0
            }
        } satisfies DrawingObjectCircle;
    } else if (input.object?.type === DrawingObjectType.polygon) {
        return {
            ...base,
            type: DrawingObjectType.polygon,
            geometry: {
                polygonCoordinates: input.object.geometry?.polygonCoordinates ?? []
            }
        } satisfies DrawingObjectPolygon;
    } else if (input.object?.type === DrawingObjectType.rectangle) {
        return {
            ...base,
            type: DrawingObjectType.rectangle,
            geometry: {
                centerUnits: input.object?.geometry?.centerUnits ?? DrawingObjectCenterUnits.latLng,
                centerX: input.object?.geometry?.centerX ?? 0,
                centerY: input.object?.geometry?.centerY ?? 0,
                sizeUnits: input.object?.geometry?.sizeUnits ?? DrawingObjectOffsetUnits.miles,
                width: input.object?.geometry?.width ?? 0,
                height: input.object?.geometry?.height ?? 0
            }
        } satisfies DrawingObjectRectangle;
    }

    throw new Error('Unrecognized drawing object type %d', input.object?.type);
}

function toApi(input: DrawingObject): ApiDrawingObject {
    const geometry: ApiDrawingObjectGeometry = {};
    if (input.type === DrawingObjectType.polygon) {
        geometry.polygonCoordinates = input.geometry.polygonCoordinates;
    } else if (input.type === DrawingObjectType.circle) {
        geometry.centerUnits = input.geometry.centerUnits;
        geometry.centerX = input.geometry.centerX;
        geometry.centerY = input.geometry.centerY;
        geometry.radiusUnits = input.geometry.radiusUnits;
        geometry.radiusX = input.geometry.radiusX;
        geometry.radiusY = input.geometry.radiusY;
    } else if (input.type === DrawingObjectType.rectangle) {
        geometry.centerUnits = input.geometry.centerUnits;
        geometry.centerX = input.geometry.centerX;
        geometry.centerY = input.geometry.centerY;
        geometry.sizeUnits = input.geometry.sizeUnits;
        geometry.width = input.geometry.width;
        geometry.height = input.geometry.height;
    } else {
        throw new Error('Unknown drawing object type');
    }

    return {
        name: input.name,
        type: input.type,
        isCompany: input.isCompany,
        label: input.label,
        line: input.line,
        fill: input.fill,
        center: input.center,
        zoom: input.zoom,
        category: input.category,
        altitude: {
            min: input.altitude.min ?? undefined,
            max: input.altitude.max ?? undefined
        },
        geometry: geometry,
        alerts: {
            plane: input.alerts.plane,
            route: input.alerts.route
        },
        validTime: {
            startDay: input.validTime.startDay ?? undefined,
            endDay: input.validTime.endDay ?? undefined,
            startTime: input.validTime.startTime ?? undefined,
            endTime: input.validTime.endTime ?? undefined,
            activeDaysOfWeek: input.validTime.activeDaysOfWeek as DayOfWeek,
            greyOutNonActive: input.validTime.greyOutNonActive
        },
        fpmProperties: input.fpmProperties
    };
}
